import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { api } from '../client';
import { queryKeys } from '../keys';
import type { PlayerAchievementBadge, RankingPageMeta } from '../types';

// ── Ladder shapes ────────────────────────────────────────────────────

export type LeaderboardSort = 'points' | 'games' | 'wins';

export interface LeaderboardRow {
  rank: number;
  playerId: number;
  name: string;
  isRegistered: boolean;
  /** Uploaded avatar URL of a registered user; null → anonymous silhouette. */
  avatar: string | null;
  level: number;
  games: number;
  wins: number;
  winPct: number;
  points: number;
  /** Career seconds played - the all-time ladder only (legacy Play Time column). */
  playTime: number | null;
  /** `playTime` as the localised label ("1h 5m", "3d 04h 12m"); null with it. */
  playTimeHuman: string | null;
  /** Servers scored on this season - the all-servers season board only. */
  servers: number | null;
  /** Up to 3 "best" (rarest-first) max-tier badges, with the tooltip payload. */
  achievements: PlayerAchievementBadge[];
}

/**
 * The season behind a season board, for the header above the standings:
 * its dates and games count (the scoped server's own count on a per-server
 * board, like the legacy page).
 */
export interface LeaderboardSeasonFacts {
  num: number;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
  gamesCount: number;
}

/**
 * `GET /api/leaderboard/top100?page=N` - custom `{ data, podium, season, meta }`
 * envelope, one 100-row page of the ladder (ranks are global across pages;
 * the all-time ladder stops at page 10, the legacy "Top 1000"). A hidden or
 * unknown `server` is a 404.
 */
export interface LeaderboardResponse {
  data: LeaderboardRow[];
  /** The ladder's top three - empty on every page but the first. */
  podium: LeaderboardRow[];
  /** Null on the all-time ladder and for an unknown season. */
  season: LeaderboardSeasonFacts | null;
  meta: RankingPageMeta & {
    season: number | null;
    /** Server the standings are scoped to, or null for all servers. */
    server: number | null;
    sort: LeaderboardSort;
  };
}

/** Positions of the season and server scope in `queryKeys.leaderboard.top100(...)`. */
const TOP100_KEY_SEASON_INDEX = 2;
const TOP100_KEY_SERVER_INDEX = 3;

export interface LeaderboardQuery {
  /** Season to scope to; omitted = the all-time ladder (career totals). */
  season?: number;
  /** Scope the standings to one server (legacy `/season/{id}/server/{server}`). */
  server?: number;
  sort?: LeaderboardSort;
  /** 1-based page of 100 rows; past the last page the board comes back empty. */
  page?: number;
}

/**
 * `GET /api/leaderboard/top100` - one 100-row page of the ladder (all-time, or
 * one season). Returns the whole `{ data, podium, meta }` envelope so the
 * screen can render the podium, the pager and echo the resolved season/sort.
 */
export function useLeaderboard(params: LeaderboardQuery = {}): UseQueryResult<LeaderboardResponse> {
  const page = params.page ?? 1;
  const queryKey = queryKeys.leaderboard.top100({ ...params, page });
  return useQuery({
    queryKey,
    // Paging/re-sorting keeps the loaded board on screen (no flicker) - but
    // only within one board: another season/server must not wear its rows.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[TOP100_KEY_SEASON_INDEX] === queryKey[TOP100_KEY_SEASON_INDEX] &&
      previousQuery.queryKey[TOP100_KEY_SERVER_INDEX] === queryKey[TOP100_KEY_SERVER_INDEX]
        ? keepPreviousData(previous)
        : undefined,
    queryFn: () =>
      api.get<LeaderboardResponse>('/leaderboard/top100', {
        query: { season: params.season, server: params.server, sort: params.sort, page },
      }),
  });
}
